import type { ReactNode } from "react";

/**
 * Per-tab section header for the Coach Workspace list pages.
 *
 * Ports the legacy `admin-panel-head` / `roster-head` pattern
 * (`index.html`): each coach sub-tab card leads with a
 * DESCRIPTIVE title (`<h3>`) + a sub-line explaining the tab,
 * with the page's action buttons on the right.
 *
 * This is distinct from — and sits below — the shell's constant
 * "Coaching Workspace" header. The tab name is never repeated:
 * the strip already shows which tab is active; this header's job
 * is to describe it.
 */

export type PageHeaderProps = {
  /** Descriptive title — e.g. "Review Playlists", not "Playlists". */
  title: string;
  /** One-line explanation of what this tab is for. */
  description: ReactNode;
  /** Optional action buttons, right-aligned on the same row. */
  actions?: ReactNode;
};

export function PageHeader({ title, description, actions }: PageHeaderProps) {
  return (
    <div
      className="grid min-w-0 gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start"
      data-testid="coach-page-header"
    >
      <div className="min-w-0">
        <h2 className="text-lg font-display text-ink">{title}</h2>
        <p className="mt-0.5 max-w-3xl text-sm leading-snug text-ink-muted">
          {description}
        </p>
      </div>
      {actions ? (
        <div
          className="min-w-0"
          data-testid="coach-page-header-actions"
        >
          <div className="flex min-w-0 flex-wrap items-center gap-2 sm:justify-end">
            {actions}
          </div>
        </div>
      ) : null}
    </div>
  );
}
